import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  FileCheck, Plus, Trash2, Search, Upload, Download, Eye, FileText, Image, Edit2
} from "lucide-react";
import BottomNav from "./BottomNav";
import { AppSidebar, SidebarMenuButton } from "./AppSidebar";

const authFetch = async (url, options = {}) => {
  const token = localStorage.getItem("session_token");
  return fetch(url, {
    ...options,
    headers: { ...options.headers, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};

export default function ConsentsPage() {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [consents, setConsents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewFile, setViewFile] = useState(null);

  // Form state
  const [form, setForm] = useState({ patient_name: "", patient_surname: "", patient_id: "" });
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  // Edit state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({ consent_id: "", patient_name: "", patient_surname: "", patient_id: "" });
  const [saving, setSaving] = useState(false);

  const fetchConsents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/consents`);
      if (res.ok) setConsents(await res.json());
    } catch (e) { console.error("Failed to fetch consents:", e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchConsents(); }, [fetchConsents]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Palun valige fail"); return; }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("patient_name", form.patient_name);
      formData.append("patient_surname", form.patient_surname);
      formData.append("patient_id", form.patient_id);
      formData.append("file", file);

      const res = await authFetch(`${API}/consents`, {
        method: "POST",
        body: formData,
      });
      if (res.ok) {
        toast.success("Nõusolek salvestatud");
        setNewDialogOpen(false);
        setForm({ patient_name: "", patient_surname: "", patient_id: "" });
        setFile(null);
        fetchConsents();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Salvestamine ebaõnnestus");
      }
    } catch (e) { toast.error("Võrguühenduse viga"); }
    finally { setUploading(false); }
  };

  const handleDelete = async (consentId) => {
    if (!window.confirm("Kas olete kindel, et soovite selle nõusoleku kustutada?")) return;
    try {
      const res = await authFetch(`${API}/consents/${consentId}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Nõusolek kustutatud");
        fetchConsents();
      }
    } catch (e) { toast.error("Kustutamine ebaõnnestus"); }
  };

  const handleViewFile = async (consent) => {
    try {
      const res = await authFetch(`${API}/consents/file/${consent.file_path}`);
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        setViewFile({ url, type: consent.file_type, name: consent.file_name });
      } else {
        toast.error("Faili laadimine ebaõnnestus");
      }
    } catch (e) { toast.error("Faili laadimine ebaõnnestus"); }
  };

  const handleDownloadFile = async (consent) => {
    try {
      const res = await authFetch(`${API}/consents/file/${consent.file_path}`);
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = consent.file_name || "consent";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (e) { toast.error("Allalaadimine ebaõnnestus"); }
  };

  const openEditDialog = (consent) => {
    setEditForm({
      consent_id: consent.consent_id,
      patient_name: consent.patient_name,
      patient_surname: consent.patient_surname,
      patient_id: consent.patient_id,
    });
    setEditDialogOpen(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await authFetch(`${API}/consents/${editForm.consent_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          patient_name: editForm.patient_name,
          patient_surname: editForm.patient_surname,
          patient_id: editForm.patient_id,
        }),
      });
      if (res.ok) {
        toast.success("Andmed uuendatud");
        setEditDialogOpen(false);
        fetchConsents();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Uuendamine ebaõnnestus");
      }
    } catch (e) { toast.error("Võrguühenduse viga"); }
    finally { setSaving(false); }
  };

  const filteredConsents = consents.filter(c => {
    const q = searchQuery.toLowerCase();
    return c.patient_name?.toLowerCase().includes(q) ||
      c.patient_surname?.toLowerCase().includes(q) ||
      c.patient_id?.includes(q);
  });

  // Group by patient
  const grouped = {};
  filteredConsents.forEach(c => {
    const key = c.patient_id;
    if (!grouped[key]) grouped[key] = { patient_name: c.patient_name, patient_surname: c.patient_surname, patient_id: c.patient_id, consents: [] };
    grouped[key].consents.push(c);
  });
  const patients = Object.values(grouped);

  const isImage = (type) => ["jpg", "jpeg", "png", "webp"].includes(type);

  return (
    <div className="min-h-screen bg-slate-50 pb-16 md:pb-0" data-testid="consents-page">
      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="md:ml-64 min-h-screen">
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200 px-4 md:px-6 py-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <SidebarMenuButton onClick={() => setSidebarOpen(true)} />
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-slate-900">Patsiendi nõusolekud</h1>
                <p className="text-sm text-slate-500">{consents.length} nõusolekut</p>
              </div>
            </div>
            <Button onClick={() => setNewDialogOpen(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="add-consent-btn">
              <Plus className="w-4 h-4 mr-2" /> Lisa nõusolek
            </Button>
          </div>
        </header>

        <main className="max-w-6xl mx-auto p-4 md:p-6 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Otsi nime või isikukoodi..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-testid="consents-search" />
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64"><div className="w-8 h-8 spinner" /></div>
          ) : patients.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="p-12 text-center">
                <FileCheck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  {searchQuery ? "Nõusolekuid ei leitud" : "Nõusolekuid pole veel lisatud"}
                </h3>
                <p className="text-sm text-slate-500">
                  {searchQuery ? "Proovige muuta otsingut" : "Lisage esimene patsiendi nõusolek"}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {patients.map(p => (
                <Card key={p.patient_id} className="border-slate-200" data-testid={`patient-${p.patient_id}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center justify-between">
                      <span>{p.patient_name} {p.patient_surname}</span>
                      <Badge variant="outline" className="text-xs font-normal">IK: {p.patient_id}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {p.consents.map(c => (
                      <div key={c.consent_id} className="flex items-center gap-2 bg-slate-50 rounded-lg p-3" data-testid={`consent-${c.consent_id}`}>
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0">
                            {isImage(c.file_type) ? <Image className="w-4 h-4 text-sky-500" /> : <FileText className="w-4 h-4 text-red-500" />}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{c.file_name}</p>
                            <p className="text-xs text-slate-400">
                              {new Date(c.created_at).toLocaleString("et-EE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button size="sm" variant="ghost" onClick={() => openEditDialog(c)} className="text-slate-600" data-testid={`edit-${c.consent_id}`}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleViewFile(c)} className="text-sky-600" data-testid={`view-${c.consent_id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDownloadFile(c)} className="text-slate-600" data-testid={`download-${c.consent_id}`}>
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(c.consent_id)} className="text-red-500" data-testid={`delete-${c.consent_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* New Consent Dialog */}
      <Dialog open={newDialogOpen} onOpenChange={setNewDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Lisa patsiendi nõusolek</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Eesnimi *</Label>
                <Input value={form.patient_name} onChange={e => setForm({ ...form, patient_name: e.target.value })} required data-testid="consent-patient-name" />
              </div>
              <div className="space-y-2">
                <Label>Perekonnanimi *</Label>
                <Input value={form.patient_surname} onChange={e => setForm({ ...form, patient_surname: e.target.value })} required data-testid="consent-patient-surname" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Isikukood (11 numbrit) *</Label>
              <Input value={form.patient_id} onChange={e => setForm({ ...form, patient_id: e.target.value.replace(/\D/g, "").slice(0, 11) })} required maxLength={11} data-testid="consent-patient-id" />
            </div>
            <div className="space-y-2">
              <Label>Nõusoleku fail (skaneeritud / foto) *</Label>
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center cursor-pointer hover:border-slate-400 hover:bg-slate-50 transition-colors"
              >
                {file ? (
                  <div className="flex items-center gap-2 justify-center">
                    {file.type.startsWith("image/") ? <Image className="w-5 h-5 text-sky-500" /> : <FileText className="w-5 h-5 text-red-500" />}
                    <span className="text-sm text-slate-700">{file.name}</span>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">Kliki või lohista fail siia</p>
                    <p className="text-xs text-slate-400 mt-1">JPG, PNG, WEBP, PDF (max 25 MB)</p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  className="hidden"
                  onChange={(e) => { if (e.target.files[0]) setFile(e.target.files[0]); }}
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={() => { setNewDialogOpen(false); setFile(null); }}>Tühista</Button>
              <Button type="submit" disabled={uploading} className="flex-1 bg-slate-900 hover:bg-slate-800" data-testid="consent-submit-btn">
                {uploading ? "Salvestan..." : "Salvesta"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* File Viewer Dialog */}
      <Dialog open={!!viewFile} onOpenChange={() => { if (viewFile?.url) URL.revokeObjectURL(viewFile.url); setViewFile(null); }}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh]">
          <DialogHeader><DialogTitle>{viewFile?.name}</DialogTitle></DialogHeader>
          <div className="overflow-auto max-h-[70vh]">
            {viewFile && isImage(viewFile.type) ? (
              <img src={viewFile.url} alt={viewFile.name} className="w-full rounded-lg" />
            ) : viewFile?.type === "pdf" ? (
              <iframe src={viewFile.url} title={viewFile.name} className="w-full h-[65vh] rounded-lg border" />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Consent Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Muuda andmeid</DialogTitle></DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Eesnimi *</Label>
                <Input value={editForm.patient_name} onChange={e => setEditForm({ ...editForm, patient_name: e.target.value })} required data-testid="edit-consent-name" />
              </div>
              <div className="space-y-2">
                <Label>Perekonnanimi *</Label>
                <Input value={editForm.patient_surname} onChange={e => setEditForm({ ...editForm, patient_surname: e.target.value })} required data-testid="edit-consent-surname" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Isikukood (11 numbrit) *</Label>
              <Input value={editForm.patient_id} onChange={e => setEditForm({ ...editForm, patient_id: e.target.value.replace(/\D/g, "").slice(0, 11) })} required maxLength={11} data-testid="edit-consent-id" />
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setEditDialogOpen(false)}>Tühista</Button>
              <Button type="submit" disabled={saving} className="flex-1 bg-slate-900 hover:bg-slate-800" data-testid="edit-consent-submit">
                {saving ? "Salvestan..." : "Salvesta"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
}
